import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { User, Mail, Shield, Camera, PenTool, Lock, Save, Eye, EyeOff, Upload, Trash2, FileText, ShieldCheck } from 'lucide-react';

const Perfil = () => {
  const { user, login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState({
    nombre: '',
    email: '',
    rol_nombre: '',
    foto_url: null,
    rubrica_url: null,
    tipo_documento: '',
    documento_identidad: ''
  });
  
  // Security settings state
  const [securitySettings, setSecuritySettings] = useState({
    two_factor_enabled: false,
    password_expires: true,
    password_never_expires: false
  });
  const [savingSecurity, setSavingSecurity] = useState(false);
  
  // Password change state
  const [showPasswordSection, setShowPasswordSection] = useState(false);
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  
  // File input refs
  const photoInputRef = useRef(null);
  const rubricaInputRef = useRef(null);

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    setLoading(true);
    try {
      const response = await api.get('/profile');
      setProfile({
        nombre: response.data.nombre || '',
        email: response.data.email || '',
        rol_nombre: response.data.rol_nombre || '',
        foto_url: response.data.foto_url || null,
        rubrica_url: response.data.rubrica_url || null,
        tipo_documento: response.data.tipo_documento || '',
        documento_identidad: response.data.documento_identidad || ''
      });
      setSecuritySettings({
        two_factor_enabled: response.data.two_factor_enabled || false,
        password_expires: response.data.password_expires !== false,
        password_never_expires: response.data.password_never_expires || false
      });
    } catch (error) {
      toast.error('Error al cargar el perfil');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    if (!profile.nombre.trim()) {
      toast.error('El nombre es requerido');
      return;
    }
    if (!profile.email.trim()) {
      toast.error('El correo electrónico es requerido');
      return;
    }

    setSaving(true);
    try {
      const response = await api.put('/profile', {
        nombre: profile.nombre,
        email: profile.email,
        tipo_documento: profile.tipo_documento,
        documento_identidad: profile.documento_identidad
      });
      setProfile(prev => ({
        ...prev,
        nombre: response.data.nombre,
        email: response.data.email,
        tipo_documento: response.data.tipo_documento || '',
        documento_identidad: response.data.documento_identidad || ''
      }));
      toast.success('Perfil actualizado correctamente');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar el perfil');
    } finally {
      setSaving(false);
    }
  };

  // Handle security settings save (only 2FA can be changed by user)
  const handleSaveSecurity = async () => {
    setSavingSecurity(true);
    try {
      await api.put('/profile/security', {
        two_factor_enabled: securitySettings.two_factor_enabled
      });
      toast.success('Configuración de seguridad actualizada');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar la configuración');
    } finally {
      setSavingSecurity(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Solo se permiten imágenes JPG, PNG o WebP');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('La imagen no debe exceder 5MB');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    try {
      toast.loading('Subiendo foto...', { id: 'photo-upload' });
      const response = await api.post('/profile/upload-photo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setProfile(prev => ({ ...prev, foto_url: response.data.foto_url }));
      toast.success('Foto actualizada', { id: 'photo-upload' });
    } catch (error) {
      toast.error('Error al subir la foto', { id: 'photo-upload' });
    }
  };

  const handleRubricaUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Solo se permiten imágenes JPG, PNG o WebP');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.error('La imagen no debe exceder 2MB');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    try {
      toast.loading('Subiendo rúbrica...', { id: 'rubrica-upload' });
      const response = await api.post('/profile/upload-rubrica', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setProfile(prev => ({ ...prev, rubrica_url: response.data.rubrica_url }));
      toast.success('Rúbrica actualizada', { id: 'rubrica-upload' });
    } catch (error) {
      toast.error('Error al subir la rúbrica', { id: 'rubrica-upload' });
    }
  };

  const handleChangePassword = async () => {
    if (!passwordData.current_password) {
      toast.error('Ingrese su contraseña actual');
      return;
    }
    if (!passwordData.new_password) {
      toast.error('Ingrese la nueva contraseña');
      return;
    }
    if (passwordData.new_password.length < 6) {
      toast.error('La nueva contraseña debe tener al menos 6 caracteres');
      return;
    }
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('Las contraseñas no coinciden');
      return;
    }

    setChangingPassword(true);
    try {
      await api.post('/profile/change-password', {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      });
      toast.success('Contraseña actualizada correctamente');
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
      setShowPasswordSection(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cambiar la contraseña');
    } finally {
      setChangingPassword(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#9d8272] mx-auto"></div>
          <p className="mt-4 text-sm text-[#5A5A5A]">Cargando perfil...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#2D2D2D]" style={{ fontFamily: 'Playfair Display, serif' }}>
          Mi Perfil
        </h1>
        <p className="text-sm lg:text-base text-[#5A5A5A] mt-1">
          Administra tu información personal y credenciales
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Photo and Signature */}
        <div className="lg:col-span-1 space-y-6">
          {/* Profile Photo Card */}
          <Card className="border-[#E8E8E7]">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Camera size={18} className="text-[#9d8272]" />
                Foto de Perfil
              </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col items-center">
              <div className="relative">
                {profile.foto_url ? (
                  <img
                    src={profile.foto_url}
                    alt="Foto de perfil"
                    className="w-32 h-32 rounded-full object-cover border-4 border-[#E8E8E7]"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div 
                  className="w-32 h-32 rounded-full bg-[#E8E8E7] flex items-center justify-center border-4 border-[#c5b8a2]"
                  style={{ display: profile.foto_url ? 'none' : 'flex' }}
                >
                  <User size={48} className="text-[#9d8272]" />
                </div>
                <button
                  onClick={() => photoInputRef.current?.click()}
                  className="absolute bottom-0 right-0 p-2 bg-[#9d8272] text-white rounded-full hover:bg-[#8a7162] transition-colors shadow-md"
                  title="Cambiar foto"
                >
                  <Camera size={16} />
                </button>
              </div>
              <input
                ref={photoInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handlePhotoUpload}
                className="hidden"
              />
              <p className="text-xs text-[#888888] mt-3 text-center">
                JPG, PNG o WebP. Máximo 5MB
              </p>
            </CardContent>
          </Card>

          {/* Signature Card */}
          <Card className="border-[#E8E8E7]">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <PenTool size={18} className="text-[#9d8272]" />
                Rúbrica / Firma
              </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col items-center">
              <div className="w-full h-24 bg-[#FAFAFA] border-2 border-dashed border-[#E8E8E7] rounded-lg flex items-center justify-center overflow-hidden">
                {profile.rubrica_url ? (
                  <img
                    src={profile.rubrica_url}
                    alt="Rúbrica"
                    className="max-w-full max-h-full object-contain p-2"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div 
                  className="text-center text-[#888888]"
                  style={{ display: profile.rubrica_url ? 'none' : 'flex', flexDirection: 'column', alignItems: 'center' }}
                >
                  <PenTool size={24} className="mx-auto mb-1 opacity-50" />
                  <p className="text-xs">Sin rúbrica</p>
                </div>
              </div>
              <input
                ref={rubricaInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleRubricaUpload}
                className="hidden"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => rubricaInputRef.current?.click()}
                className="mt-3 border-[#9d8272] text-[#9d8272]"
              >
                <Upload size={14} className="mr-2" />
                {profile.rubrica_url ? 'Cambiar Rúbrica' : 'Subir Rúbrica'}
              </Button>
              <p className="text-xs text-[#888888] mt-2 text-center">
                Imagen con fondo transparente o blanco. Máximo 2MB
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Profile Info and Password */}
        <div className="lg:col-span-2 space-y-6">
          {/* Profile Information Card */}
          <Card className="border-[#E8E8E7]">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <User size={20} className="text-[#9d8272]" />
                Información Personal
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium flex items-center gap-2">
                    <User size={14} className="text-[#9d8272]" />
                    Nombre Completo
                  </Label>
                  <Input
                    value={profile.nombre}
                    onChange={(e) => setProfile(prev => ({ ...prev, nombre: e.target.value }))}
                    className="mt-1 border-[#c5b8a2]"
                    placeholder="Tu nombre completo"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium flex items-center gap-2">
                    <Mail size={14} className="text-[#9d8272]" />
                    Correo Electrónico
                  </Label>
                  <Input
                    type="email"
                    value={profile.email}
                    onChange={(e) => setProfile(prev => ({ ...prev, email: e.target.value }))}
                    className="mt-1 border-[#c5b8a2]"
                    placeholder="tu@email.com"
                  />
                </div>
              </div>
              
              <div>
                <Label className="text-sm font-medium flex items-center gap-2">
                  <Shield size={14} className="text-[#9d8272]" />
                  Rol
                </Label>
                <Input
                  value={profile.rol_nombre}
                  readOnly
                  className="mt-1 bg-gray-50 border-[#E8E8E7] text-[#888888] cursor-not-allowed"
                />
                <p className="text-xs text-[#888888] mt-1">
                  El rol solo puede ser modificado por un administrador
                </p>
              </div>

              {/* Tipo de Documento y Documento de Identidad */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium flex items-center gap-2">
                    <FileText size={14} className="text-[#9d8272]" />
                    Tipo de Documento
                  </Label>
                  <Select
                    value={profile.tipo_documento}
                    onValueChange={(value) => setProfile(prev => ({ ...prev, tipo_documento: value }))}
                  >
                    <SelectTrigger className="mt-1 border-[#c5b8a2]">
                      <SelectValue placeholder="Seleccionar tipo" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="DNI">DNI</SelectItem>
                      <SelectItem value="CE">Carné de Extranjería</SelectItem>
                      <SelectItem value="Pasaporte">Pasaporte</SelectItem>
                      <SelectItem value="RUC">RUC</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm font-medium flex items-center gap-2">
                    <FileText size={14} className="text-[#9d8272]" />
                    Documento de Identidad
                  </Label>
                  <Input
                    value={profile.documento_identidad}
                    onChange={(e) => setProfile(prev => ({ ...prev, documento_identidad: e.target.value }))}
                    className="mt-1 border-[#c5b8a2]"
                    placeholder="Número de documento"
                  />
                </div>
              </div>

              <div className="flex justify-end pt-2">
                <Button
                  onClick={handleSaveProfile}
                  disabled={saving}
                  className="bg-[#9d8272] hover:bg-[#8a7162] text-white"
                >
                  <Save size={16} className="mr-2" />
                  {saving ? 'Guardando...' : 'Guardar Cambios'}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Security Settings Card */}
          <Card className="border-[#E8E8E7]">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <ShieldCheck size={20} className="text-[#9d8272]" />
                Seguridad
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* 2FA Checkbox - Editable */}
              <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                <Checkbox
                  id="two_factor_enabled"
                  checked={securitySettings.two_factor_enabled}
                  onCheckedChange={(checked) => setSecuritySettings(prev => ({ ...prev, two_factor_enabled: checked }))}
                />
                <div className="flex-1">
                  <Label htmlFor="two_factor_enabled" className="text-sm font-medium cursor-pointer">
                    Habilitar 2FA por correo
                  </Label>
                  <p className="text-xs text-[#888888]">
                    Recibirás un código de verificación en tu correo al iniciar sesión
                  </p>
                </div>
              </div>

              {/* Password Expires Checkbox - Read Only */}
              <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg opacity-70">
                <Checkbox
                  id="password_expires"
                  checked={securitySettings.password_expires}
                  disabled
                />
                <div className="flex-1">
                  <Label htmlFor="password_expires" className="text-sm font-medium text-[#888888]">
                    Cambio obligatorio de contraseña cada 90 días
                  </Label>
                  <p className="text-xs text-[#888888]">
                    Política de seguridad establecida por el administrador
                  </p>
                </div>
              </div>

              {/* Password Never Expires Checkbox - Read Only */}
              <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg opacity-70">
                <Checkbox
                  id="password_never_expires"
                  checked={securitySettings.password_never_expires}
                  disabled
                />
                <div className="flex-1">
                  <Label htmlFor="password_never_expires" className="text-sm font-medium text-[#888888]">
                    Contraseña no caduca
                  </Label>
                  <p className="text-xs text-[#888888]">
                    Solo puede ser modificado por un administrador
                  </p>
                </div>
              </div>

              <div className="flex justify-end pt-2">
                <Button
                  onClick={handleSaveSecurity}
                  disabled={savingSecurity}
                  className="bg-[#9d8272] hover:bg-[#8a7162] text-white"
                >
                  <Save size={16} className="mr-2" />
                  {savingSecurity ? 'Guardando...' : 'Guardar Seguridad'}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Change Password Card */}
          <Card className="border-[#E8E8E7]">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Lock size={20} className="text-[#9d8272]" />
                  Cambiar Contraseña
                </CardTitle>
                {!showPasswordSection && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowPasswordSection(true)}
                    className="border-[#9d8272] text-[#9d8272]"
                  >
                    Cambiar
                  </Button>
                )}
              </div>
            </CardHeader>
            {showPasswordSection && (
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-sm font-medium">Contraseña Actual</Label>
                  <div className="relative mt-1">
                    <Input
                      type={showCurrentPassword ? 'text' : 'password'}
                      value={passwordData.current_password}
                      onChange={(e) => setPasswordData(prev => ({ ...prev, current_password: e.target.value }))}
                      className="border-[#c5b8a2] pr-10"
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#888888] hover:text-[#5A5A5A]"
                    >
                      {showCurrentPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium">Nueva Contraseña</Label>
                    <div className="relative mt-1">
                      <Input
                        type={showNewPassword ? 'text' : 'password'}
                        value={passwordData.new_password}
                        onChange={(e) => setPasswordData(prev => ({ ...prev, new_password: e.target.value }))}
                        className="border-[#c5b8a2] pr-10"
                        placeholder="Mínimo 6 caracteres"
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-[#888888] hover:text-[#5A5A5A]"
                      >
                        {showNewPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Confirmar Contraseña</Label>
                    <Input
                      type={showNewPassword ? 'text' : 'password'}
                      value={passwordData.confirm_password}
                      onChange={(e) => setPasswordData(prev => ({ ...prev, confirm_password: e.target.value }))}
                      className="mt-1 border-[#c5b8a2]"
                      placeholder="Repetir contraseña"
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-2 pt-2">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowPasswordSection(false);
                      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
                    }}
                    className="border-[#E8E8E7]"
                  >
                    Cancelar
                  </Button>
                  <Button
                    onClick={handleChangePassword}
                    disabled={changingPassword}
                    className="bg-[#9d8272] hover:bg-[#8a7162] text-white"
                  >
                    <Lock size={16} className="mr-2" />
                    {changingPassword ? 'Cambiando...' : 'Cambiar Contraseña'}
                  </Button>
                </div>
              </CardContent>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Perfil;
